import type { MensagemSala, VisaoSala } from '../../server/sala';
import { app } from './app';
import { somAviso } from './som';
import { esc } from './util';

/**
 * Bate-papo da sala online. Fica FORA da raiz do jogo (#app): o jogo redesenha a tela várias vezes por segundo e isso
 * apagaria o texto que está sendo digitado. Também mostra os avisos do jogo (ofertas, leilão, rodada, intervalo) como
 * notificações no canto da tela.
 */

type Enviar = (texto: string) => Promise<unknown>;

let enviarTexto: Enviar | null = null;
let raiz: HTMLElement | null = null;
let ultimoVisto = 0; // maior id de mensagem que o técnico já viu com o painel aberto
let ultimoNotificado = -1; // maior id já anunciado (toast); -1 = ainda não recebeu a primeira visão
let naoLidas = 0;
let salaAtual = '';

function el<T extends HTMLElement>(seletor: string): T {
  return (raiz as HTMLElement).querySelector(seletor) as T;
}

function hora(t: number): string {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function linha(m: MensagemSala): string {
  if (m.tipo === 'aviso') return `<li class="aviso"><span class="chat-hora">${hora(m.t)}</span> ${esc(m.texto)}</li>`;
  const meu = m.de === app.online?.visao?.eu.nome;
  return `<li class="msg${meu ? ' minha' : ''}"><span class="chat-hora">${hora(m.t)}</span> <b>${esc(m.de ?? '?')}</b>: ${esc(m.texto)}</li>`;
}

function desenharLista(msgs: MensagemSala[]): void {
  const ul = el<HTMLElement>('.chat-lista');
  const noFim = ul.scrollHeight - ul.scrollTop - ul.clientHeight < 40;
  ul.innerHTML = msgs.map(linha).join('') || '<li class="cinza">Nenhuma mensagem ainda. Diga olá!</li>';
  if (noFim || !ul.dataset.rolado) {
    ul.scrollTop = ul.scrollHeight;
    ul.dataset.rolado = '1';
  }
}

function toast(texto: string, tipo: 'aviso' | 'chat'): void {
  const caixa = el<HTMLElement>('.chat-toasts');
  const t = document.createElement('div');
  t.className = `chat-toast ${tipo}`;
  t.textContent = texto;
  caixa.appendChild(t);
  while (caixa.children.length > 4) caixa.firstElementChild?.remove();
  window.setTimeout(() => t.remove(), tipo === 'aviso' ? 7000 : 5000);
}

function painelAberto(): boolean {
  return !el<HTMLElement>('.chat-painel').hidden;
}

function marcarLidas(msgs: MensagemSala[]): void {
  ultimoVisto = msgs.reduce((mx, m) => Math.max(mx, m.id), ultimoVisto);
  naoLidas = 0;
  atualizarContador();
}

function atualizarContador(): void {
  const n = el<HTMLElement>('.chat-n');
  n.textContent = String(naoLidas);
  n.hidden = naoLidas === 0;
}

/** Chamado a cada visão nova da sala: atualiza a lista, o contador e as notificações. */
export function chatNovaVisao(v: VisaoSala): void {
  if (!raiz) return;
  const msgs = v.sala.chat ?? [];
  if (salaAtual !== v.sala.codigo) {
    // sala nova: não anuncia o histórico
    salaAtual = v.sala.codigo;
    ultimoNotificado = msgs.reduce((mx, m) => Math.max(mx, m.id), 0);
    ultimoVisto = ultimoNotificado;
    naoLidas = 0;
    atualizarContador();
  }
  const aberto = painelAberto();
  const novas = msgs.filter((m) => m.id > ultimoNotificado);
  for (const m of novas) {
    const minha = m.tipo === 'chat' && m.de === v.eu.nome;
    if (m.tipo === 'aviso') {
      toast(m.texto, 'aviso');
      somAviso();
    } else if (!minha && !aberto) {
      toast(`${m.de}: ${m.texto}`, 'chat');
      somAviso();
    }
    if (!aberto && !minha) naoLidas++;
  }
  if (novas.length) ultimoNotificado = Math.max(ultimoNotificado, ...novas.map((m) => m.id));
  atualizarContador();
  raiz.hidden = false;
  desenharLista(msgs);
  if (aberto) marcarLidas(msgs);
}

function alternar(): void {
  const painel = el<HTMLElement>('.chat-painel');
  painel.hidden = !painel.hidden;
  if (!painel.hidden) {
    marcarLidas(app.online?.visao?.sala.chat ?? []);
    const ul = el<HTMLElement>('.chat-lista');
    ul.scrollTop = ul.scrollHeight;
    el<HTMLInputElement>('.chat-form input').focus();
  }
}

/** Cria o painel (uma vez) e liga o envio de mensagens. */
export function iniciarChat(enviar: Enviar): void {
  enviarTexto = enviar;
  raiz = document.createElement('div');
  raiz.id = 'chat';
  raiz.className = 'chat';
  raiz.hidden = true;
  raiz.innerHTML = `<div class="chat-toasts" aria-live="polite"></div>
    <div class="chat-painel" hidden>
      <div class="chat-cab"><b>Bate-papo da sala</b><button type="button" class="chat-x" aria-label="Fechar bate-papo">×</button></div>
      <ul class="chat-lista"></ul>
      <form class="chat-form" autocomplete="off"><input type="text" maxlength="240" placeholder="Escreva uma mensagem…" aria-label="Mensagem" /><button type="submit" class="btn principal">Enviar</button></form>
    </div>
    <button type="button" class="chat-botao" aria-label="Abrir bate-papo">💬<span class="chat-n" hidden>0</span></button>`;
  document.body.appendChild(raiz);
  el<HTMLElement>('.chat-botao').addEventListener('click', alternar);
  el<HTMLElement>('.chat-x').addEventListener('click', alternar);
  el<HTMLFormElement>('.chat-form').addEventListener('submit', (ev) => {
    ev.preventDefault();
    const campo = el<HTMLInputElement>('.chat-form input');
    const texto = campo.value.trim();
    if (!texto || !enviarTexto) return;
    campo.value = '';
    enviarTexto(texto).catch((erro: unknown) => toast(erro instanceof Error ? erro.message : 'não foi possível enviar', 'aviso'));
  });
  // some quando o técnico sai da sala
  window.setInterval(() => {
    if (!app.online && raiz) {
      raiz.hidden = true;
      salaAtual = '';
      el<HTMLElement>('.chat-painel').hidden = true;
    }
  }, 1000);
}
